import PropTypes from 'prop-types';
import { Card, Box, Typography, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme, alpha } from '@mui/material/styles';
import CountUp from 'react-countup';

const KPICard = ({ title, count, ratio, isActive, onClick, color = 'primary', loading = false, icon }) => {
    const theme = useTheme();

    const variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <motion.div variants={variants} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} style={{ height: '100%' }}>
            <Card
                onClick={onClick}
                sx={{
                    height: '100%',
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    cursor: 'pointer',
                    p: 3,
                    borderRadius: 4,
                    boxShadow: isActive
                        ? `0 0 0 2px ${theme.palette[color].main}, 0 12px 24px -10px ${alpha(theme.palette[color].main, 0.4)}`
                        : '0 2px 8px rgba(0,0,0,0.05)',
                    border: isActive ? '1px solid transparent' : `1px solid ${theme.palette.grey[300]}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: 160, // Increased height for better spacing
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                }}
            >
                {/* Decorative floating shapes - Subtle colored blobs */}
                {isActive && (
                    <>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -40,
                                right: -40,
                                width: 140,
                                height: 140,
                                borderRadius: '50%',
                                background: alpha(theme.palette[color].main, 0.1),
                                zIndex: 0
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: -30,
                                left: -30,
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                background: alpha(theme.palette[color].main, 0.05),
                                zIndex: 0
                            }}
                        />
                    </>
                )}

                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                opacity: 0.7,
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                lineHeight: 1.5,
                                maxWidth: '75%'
                            }}
                        >
                            {loading ? <Skeleton width="100px" /> : title}
                        </Typography>

                        {/* Icon Container */}
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isActive ? alpha(theme.palette[color].main, 0.2) : theme.palette.grey[100],
                                color: isActive ? theme.palette[color].main : theme.palette.grey[500],
                                transition: 'all 0.3s ease',
                                fontSize: '1.4rem'
                            }}
                        >
                            {loading ? <Skeleton variant="circular" width={32} height={32} /> : icon}
                        </Box>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                        <Typography
                            variant="h2"
                            sx={{ color: isActive ? theme.palette[color].main : 'inherit', fontWeight: 700, fontSize: '2.5rem' }}
                        >
                            {loading ? (
                                <Skeleton width={60} height={50} />
                            ) : (
                                <CountUp end={count ? Number(count) : 0} duration={2} separator="," />
                            )}
                        </Typography>

                        {loading ? (
                            <Skeleton width={40} height={24} />
                        ) : (
                            ratio && (
                                <Box
                                    sx={{
                                        bgcolor: alpha(theme.palette[color].main, 0.1),
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 2
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.palette[color].dark
                                        }}
                                    >
                                        {ratio}
                                    </Typography>
                                </Box>
                            )
                        )}
                    </Box>
                </Box>
            </Card>
        </motion.div>
    );
};

KPICard.propTypes = {
    title: PropTypes.string,
    count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ratio: PropTypes.string,
    isActive: PropTypes.bool,
    onClick: PropTypes.func,
    color: PropTypes.string,
    loading: PropTypes.bool,
    icon: PropTypes.node
};

export default KPICard;
